import type {
  NotificationDomain,
  NotificationEvent,
  NotificationPreferences,
  NotificationTopic,
} from '../types/notifications'

export const DOMAIN_LABEL: Record<NotificationDomain, string> = {
  security: 'Security',
  money: 'Money',
  safety: 'Safety',
  admissions: 'Applications and admissions',
  academics: 'Academics and learning',
  housing: 'Housing',
  marketplace: 'Marketplace',
  community: 'Community and social',
  opportunities: 'Opportunities',
  campagift: 'CampaGift',
  services: 'Academic services',
  notes: 'Notes',
  account: 'Your account',
}

/**
 * Topics a person controls. Security, safety and money are not here on purpose:
 * a notification that someone signed into your account, that a campus is unsafe,
 * or that money moved, is not a preference.
 */
export const TOPIC_ROWS: { key: NotificationTopic; label: string; description: string }[] = [
  {
    key: 'admissions',
    label: 'Applications and admissions',
    description: 'Requirements, deadlines and decisions on applications you have started.',
  },
  {
    key: 'academics',
    label: 'Academics and learning',
    description: 'Registration windows, assignment deadlines, results and course materials.',
  },
  {
    key: 'housing',
    label: 'Housing',
    description: 'Application progress, viewing arrangements and rent reminders.',
  },
  {
    key: 'marketplace',
    label: 'Marketplace',
    description: 'Orders, delivery, escrow outcomes and reviews.',
  },
  {
    key: 'community',
    label: 'Community and social',
    description: 'Messages, group activity, events and connection requests.',
  },
  {
    key: 'opportunities',
    label: 'Opportunities',
    description: 'Roles matching your career profile and application stage changes.',
  },
  {
    key: 'campagift',
    label: 'CampaGift',
    description: 'Gifts received, redeemed and approaching expiry.',
  },
  {
    key: 'services',
    label: 'Academic services',
    description: 'Request progress, information requests and collection.',
  },
  { key: 'notes', label: 'Notes', description: 'Reminders you set, shares and comments.' },
  {
    key: 'account',
    label: 'Your account',
    description: 'Verification progress, profile completeness and consent updates.',
  },
]

export const ALWAYS_SENT: { label: string; reason: string }[] = [
  {
    label: 'Security events',
    reason: 'A sign-in from a new device, or a password, email or PIN change.',
  },
  {
    label: 'Safety alerts',
    reason: 'Anything affecting your physical safety on or around campus.',
  },
  {
    label: 'Money',
    reason: 'Anything that moves value in your ledger, including reversals and disputes.',
  },
]

export const defaultPreferences: NotificationPreferences = {
  channels: { inApp: true, email: true, push: false, sms: false },
  topics: Object.fromEntries(TOPIC_ROWS.map((row) => [row.key, true])) as Record<
    NotificationTopic,
    boolean
  >,
}

/**
 * Seeded demonstration events. Labelled as such in the centre so nobody mistakes
 * them for their own activity.
 */
export const seedNotifications: NotificationEvent[] = [
  {
    id: 'ntf-sec-1',
    domain: 'security',
    priority: 'critical',
    title: 'New sign-in from Chrome on Windows',
    detail:
      'Lagos, Nigeria. If this was not you, change your password and sign out everywhere immediately.',
    time: '12 minutes ago',
    minutesAgo: 12,
    read: false,
    archived: false,
    to: '/settings',
    actionLabel: 'Review sessions',
    reference: 'Session SESS-4471',
    deliveries: [
      { channel: 'inApp', state: 'delivered' },
      { channel: 'email', state: 'delivered' },
      {
        channel: 'sms',
        state: 'not_configured',
        note: 'No verified phone number on your account.',
      },
    ],
  },
  {
    id: 'ntf-safe-1',
    domain: 'safety',
    priority: 'critical',
    title: 'Campus alert — Akoka gate 2 closed',
    detail: 'Power cable repair works. Use gate 1 until 18:00 today.',
    time: '18 minutes ago',
    minutesAgo: 18,
    read: false,
    archived: false,
    to: '/support',
    actionLabel: 'Open Support',
    reference: 'Issued by University of Lagos',
    deliveries: [
      { channel: 'inApp', state: 'delivered' },
      { channel: 'email', state: 'delivered' },
    ],
  },
  {
    id: 'ntf-money-1',
    domain: 'money',
    priority: 'high',
    title: 'Escrow released to Adaeze O.',
    detail:
      '₦18,000 released for ORD-40128 after you approved delivery. The paired ledger entries are on the transaction.',
    time: '1 hour ago',
    minutesAgo: 60,
    read: false,
    archived: false,
    to: '/money',
    actionLabel: 'View transaction',
    reference: 'TXN-000914',
    deliveries: [
      { channel: 'inApp', state: 'delivered' },
      { channel: 'email', state: 'delivered' },
    ],
  },
  {
    id: 'ntf-acad-1',
    domain: 'academics',
    priority: 'high',
    title: 'SYE 305 assignment due tomorrow',
    detail: 'Control systems problem set, 09:00. Submission is through your department, not Campuzon.',
    time: '2 hours ago',
    minutesAgo: 120,
    read: false,
    archived: false,
    to: '/academics',
    actionLabel: 'Open Academics',
    reference: 'SYE 305',
    deliveries: [
      { channel: 'inApp', state: 'delivered' },
      { channel: 'email', state: 'delivered' },
    ],
  },
  {
    id: 'ntf-house-1',
    domain: 'housing',
    priority: 'normal',
    title: 'Cedarwood Residence is reviewing your application',
    detail: 'They have everything they asked for. Typical decision time at this property is 4 days.',
    time: '5 hours ago',
    minutesAgo: 300,
    read: false,
    archived: false,
    to: '/housing',
    actionLabel: 'Open application',
    reference: 'APP-2291',
    deliveries: [{ channel: 'inApp', state: 'delivered' }],
  },
  {
    id: 'ntf-comm-1',
    domain: 'community',
    priority: 'normal',
    title: 'Chidera replied in Systems Engineering 300L',
    detail: '“Does anyone have the corrected lab schedule for week 9?”',
    time: '6 hours ago',
    minutesAgo: 360,
    read: false,
    archived: false,
    to: '/community',
    actionLabel: 'Open thread',
    reference: 'Group · Systems Engineering 300L',
    deliveries: [
      { channel: 'inApp', state: 'delivered' },
      {
        channel: 'push',
        state: 'not_configured',
        note: 'Push needs a Campuzon app installed on your device.',
      },
    ],
  },
  {
    id: 'ntf-notes-1',
    domain: 'notes',
    priority: 'normal',
    title: 'Reminder — Revise control systems notes',
    detail: 'You set this reminder for today at 19:00.',
    time: '7 hours ago',
    minutesAgo: 420,
    read: true,
    archived: false,
    to: '/notes',
    actionLabel: 'Open note',
    reference: 'Note · Control systems',
    deliveries: [{ channel: 'inApp', state: 'delivered' }],
  },
  {
    id: 'ntf-opp-1',
    domain: 'opportunities',
    priority: 'normal',
    title: 'Paystack moved you to interview',
    detail:
      'Software engineering internship. The employer sets the stage; Campuzon reports what they recorded.',
    time: 'Yesterday',
    minutesAgo: 1500,
    read: true,
    archived: false,
    to: '/opportunities',
    actionLabel: 'View application',
    reference: 'APP-JOB-118',
    deliveries: [
      { channel: 'inApp', state: 'delivered' },
      { channel: 'email', state: 'failed', note: 'The provider rejected the address. We retried twice.' },
    ],
  },
  {
    id: 'ntf-svc-1',
    domain: 'services',
    priority: 'high',
    title: 'Transcript request needs one more document',
    detail: 'The registry has asked for your payment receipt before they process it.',
    time: 'Yesterday',
    minutesAgo: 1620,
    read: false,
    archived: false,
    to: '/services',
    actionLabel: 'Open request',
    reference: 'SRV-3310',
    deliveries: [
      { channel: 'inApp', state: 'delivered' },
      { channel: 'email', state: 'delivered' },
    ],
  },
  {
    id: 'ntf-gift-1',
    domain: 'campagift',
    priority: 'normal',
    title: 'CampaGift expiring in 14 days',
    detail: '₦2,500 remaining on your Books gift. Anything unused is recorded, not refunded.',
    time: '2 days ago',
    minutesAgo: 2900,
    read: true,
    archived: false,
    to: '/money/campagift',
    actionLabel: 'View gift',
    reference: 'GIFT-88214',
    deliveries: [{ channel: 'inApp', state: 'delivered' }],
  },
  {
    id: 'ntf-acct-1',
    domain: 'account',
    priority: 'low',
    title: 'Academic verification expires in 30 days',
    detail:
      'Revalidate to keep your verified student standing. It does not disappear — it stops asserting you are currently enrolled.',
    time: '3 days ago',
    minutesAgo: 4300,
    read: true,
    archived: false,
    to: '/identity',
    actionLabel: 'Revalidate',
    deliveries: [{ channel: 'inApp', state: 'delivered' }],
  },
  {
    id: 'ntf-mkt-1',
    domain: 'marketplace',
    priority: 'normal',
    title: 'Your review of Adaeze O. is published',
    detail: 'Reviews cannot be edited after 48 hours, and the provider can respond once.',
    time: '4 days ago',
    minutesAgo: 5800,
    read: true,
    archived: true,
    to: '/marketplace',
    actionLabel: 'View order',
    reference: 'ORD-40128',
    deliveries: [{ channel: 'inApp', state: 'delivered' }],
  },
]
